import React, { FC, useCallback, useEffect, useState } from 'react';
import { BrandVM, TrackingEventStatus, TrackingEventType } from '../../../common/view-model';
import { RootState } from '../../../redux/AppReducer';
import { aboutBrandState } from '../model';
import { AppActions } from '../../../redux/AppActions';
import { ExpoEastOverlay } from '../../ExpoEastOverlay/ExpoEastOverlay';
import { handleTracking } from '../../../helpers/handelTracking';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import ShareMenuComponent from '../../shareMenu/shareMenuComponent';
import { BoothNumberComponent } from '../../UI/BoothNumberComponent/BoothNumberComponent';
import { ExpoLabelComponent } from '../../UI/ExpoLabelComponent/ExpoLabelComponent';
import BrandAttributesComponent from '../brand-attributes/BrandAttributesComponent';
import './BrandHeaderComponent.scss';
import { UserActions } from '../../../redux/UserActions';
import { validateUrl } from '../../../helpers/validateUrl';
import StarLogo from '../../../assets/images/attributes-decorator.svg';
import ImageError from '../../../assets/images/no-image.png';
import { validateImage } from '../../../helpers/validateImagesCanLoad';
import { useAppDispatch } from '../../../redux/store';

interface IBrandHeaderComponentProps {
	brand: BrandVM;
	showShareMenu: boolean;
}

interface ILinksComponentProps {
	brand: BrandVM;
	isMobile?: boolean;
}

const LinksComponent: FC<ILinksComponentProps> = ({ brand, isMobile }) => {
	return (
		<div className={`demo-link-container ${isMobile ? 'mobile' : ''}`}>
			{brand.demo_link_1?.title && (
				<div className='item'>
					<i className='ri-external-link-line'></i>
					<a
						data-testid='link-content'
						className='attribute-link'
						href={validateUrl(brand.demo_link_1.url!)}
						target='_blank'
					>
						{brand.demo_link_1.title}
					</a>
				</div>
			)}
			{brand.demo_link_2?.title && (
				<div className='item'>
					<i className='ri-external-link-line'></i>
					<a
						data-testid='link-content'
						className='attribute-link'
						href={validateUrl(brand.demo_link_2.url!)}
						target='_blank'
					>
						{brand.demo_link_2.title}
					</a>
				</div>
			)}
		</div>
	);
};

const BrandHeaderComponent: FC<IBrandHeaderComponentProps> = ({ brand, showShareMenu }) => {
	const [state] = useState({ ...aboutBrandState });
	const [brandState, setBrandState] = useState(brand);
	const userState = useSelector((state: RootState) => state.appState.userState?.user);
	const dispatch = useAppDispatch();
	const [width, setWidth] = useState(window.innerWidth - 1);
	const currentEvent = useSelector(
		(state: RootState) => state.appState.storeState?.getCurrentEvent
	);
	const registerUserClickEvent = (event: string) =>
		dispatch(UserActions.registerUserClickEvent(event));
	useEffect(() => {
		const handleResize = () => {
			let tempWidth = document.querySelector('[data-testid="navbar"]')?.scrollWidth;
			setWidth(tempWidth! - 1);
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);
	const validateImageProfile = async () => {
		if (!brandState.url_logo) {
			setBrandState({
				...brandState,
				url_logo: ImageError
			});
			return;
		}
		const isValid = await validateImage(brandState.url_logo);
		const result = isValid ? brandState.url_logo : ImageError;
		setBrandState({
			...brandState,
			url_logo: result
		});
	};
	useEffect(() => {
		validateImageProfile();
	}, []);

	const openExpoPopUpCallback = useCallback(() => {
		dispatch(
			AppActions.setModalContent({
				content: <ExpoEastOverlay />,
				showCloseIcon: false,
				canScroll: true,
				closeOnDocumentClick: true
			})
		);
		handleTracking({
			transactionId: uuidv4(),
			entityId: brand?.id!,
			status: TrackingEventStatus.SUCCESS,
			type: TrackingEventType.CLICK_ON_SEE_US_EXPO_BUTTON,
			timeStamp: new Date().toISOString(),
			eventPayload: {
				id: currentEvent?.id!,
				name: currentEvent?.name!,
				type: brand?.name!,
				url: window.location.href,
				tags: [],
				metadata: {
					user_id: userState?.id,
					user_type: userState?.user_type,
					environment: import.meta.env.VITE_ENV_NAME,
					event_id: currentEvent?.id,
					event_name: currentEvent?.name
				}
			}
		});
	}, [dispatch]);

	const brandAttributes = brandState.brand_attributes?.split(';');

	return (
		<>
			<div className='brand-profile'>
				<div className='brand-profile-name'>
					<span className='brand-name'>{brandState.name}</span>
				</div>

				<div className='brand-information'>
					<div className='brand-image'>
						<div className='image-container'>
							<img src={brandState.url_logo} alt='brand' />
							{showShareMenu && (
								<div className='share-container'>
									<ShareMenuComponent id={brand.id} type='brand'></ShareMenuComponent>
								</div>
							)}
						</div>
					</div>
					<div className='brand-attributes-container'>
						{brandState.boothNumber && currentEvent && currentEvent != null && (
							<div className='top-attributes'>
								<BoothNumberComponent
									text={`Booth #${brandState.boothNumber}${
										brandState.hall_name ? ` ${brandState.hall_name}` : ''
									}`}
									boothStyle='primary'
									icon='ri-store-3-line'
								/>
								<ExpoLabelComponent
									text={`See us at ${currentEvent?.label || currentEvent.name}!`}
									labelStyle={`large ${currentEvent?.name
										.replace(/\s+/g, '')
										.toLocaleLowerCase()
										.trim()}`}
									icon=''
									showAnimation={true}
									onClick={openExpoPopUpCallback}
								/>
							</div>
						)}
						<div className='brand-attributes'>
							<div className='attributes-container'>
								{brandState.attributes.map((attribute, index) => {
									return (
										<BrandAttributesComponent
											key={index}
											{...attribute}
											registerUserClickEvent={registerUserClickEvent}
										/>
									);
								})}
							</div>
						</div>
						{(brandState.demo_link_1?.title || brandState.demo_link_2?.title) && (
							<LinksComponent brand={brandState} isMobile={false}></LinksComponent>
						)}
					</div>
				</div>

				{(brand.demo_link_1?.title || brand.demo_link_2?.title) && (
					<LinksComponent brand={brand} isMobile={true}></LinksComponent>
				)}

				{brandAttributes && brandState.brand_attributes?.length !== 0 && (
					<div className='container-slider-certifications' style={{ width: width }}>
						<div className='slider-certifications'>
							<div className='group'>
								{[...Array(brandAttributes.length == 1 ? 10 : 5)].map((_, i) => (
									<React.Fragment key={`group_${i}`}>
										{brandAttributes.map((attr, j) => (
											<React.Fragment key={`certification_${i}_${j}`}>
												<div className='attribute'>{attr}</div>
												<img src={StarLogo} alt='img' />
											</React.Fragment>
										))}
									</React.Fragment>
								))}
							</div>
						</div>
					</div>
				)}

				{(brandState.brand_mission || brandState.brand_story) && (
					<div
						className={`brand-details ${
							brandState.brand_mission && brandState.brand_story ? 'full-width' : ''
						}`}
					>
						<div className={`mission-container no-card `}>
							{brand.brand_mission && (
								<div className='mission-item'>
									<span className='brand-info-title'>{state.missionStatementTitleText}</span>
									<p className='brand-mission'>{brand.brand_mission}</p>
								</div>
							)}
							{brandState.brand_story && (
								<div className='mission-item'>
									<span className='brand-info-title'>{state.ourStoryTitleText}</span>
									<p className='brand-story'>{brandState.brand_story}</p>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default BrandHeaderComponent;
